import { Stack, Typography } from '@mui/material';
import { login, register } from 'API/account';
import { getAvailableProducts, getFullLicense } from 'API/license';
import { getUserInfo } from 'API/user';
import { ErrorMessages, Routes } from 'enums';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiLink from 'features/ui/base-components/pai-link';
import { useEmailForm } from 'hooks/use-email-form';
import { useExtensionAuth } from 'hooks/use-extension-auth';
import { usePasswordFrom } from 'hooks/use-password-form';
import i18n from 'i18n';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { buildRegisterPayload, readAttributionFromBrowser } from 'features/engagement/signup/attribution';
import useErrorStore from 'stores/error-store';
import useUserStore from 'stores/user-store';
import { makeStyles } from 'tss-react/mui';
import { UserInfo } from 'types/user.types';
import { trackRegistrationComplete } from 'utils/tracking';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiTextField from '../features/ui/base-components/pai-text-field';

const SignUp: React.FC = () => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const signIn = useUserStore((state) => state.signIn);
  const setToken = useUserStore((state) => state.setToken);
  const { extensionSignIn } = useExtensionAuth();

  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [companyName, setCompanyName] = React.useState('');

  const [emailState, onEmailFormChange] = useEmailForm(undefined, true);
  const [passState, onPassFormChange] = usePasswordFrom();

  const [emailHelperText, setEmailHelperText] = useState('');
  const [passwordHelperText, setPasswordHelperText] = useState('');

  // Explicit consent is required before an account can be created — the button below
  // stays disabled until this is checked.
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Optional consents (Engagement Center). Never pre-ticked — § 7 UWG needs an active opt-in.
  const [consentMarketingEmail, setConsentMarketingEmail] = useState(false);
  const [consentAdMeasurement, setConsentAdMeasurement] = useState(false);
  // Read once on arrival: where the visitor came from (ad parameters the website put in the link).
  const [attribution] = useState(() => readAttributionFromBrowser(location.search));

  const agbUrl = '/dashboard/legal/agb.html';
  const privacyUrl = 'https://secret-ai-agent.de/datenschutz';
  const dpaUrl = '/dashboard/legal/avv.html';

  useEffect(() => setEmailHelperText(''), [emailState]);
  useEffect(() => setPasswordHelperText(''), [passState]);

  const isAllValid = () =>
    termsAccepted &&
    passState.inputValue.trim() !== '' &&
    emailState.inputValue.trim() !== '' &&
    companyName.trim() !== '' &&
    companyName.length <= 30 &&
    firstName.trim() !== '' &&
    lastName.trim() !== '' &&
    firstName.trim().length <= 128 &&
    lastName.trim().length <= 128;

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value);
  };

  const handleCompanyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyName(event.target.value);
  };

  const handleSignUp = async () => {
    setEmailHelperText('');
    setPasswordHelperText('');

    if (emailState.error) {
      setEmailHelperText(emailState.helperText);
      return;
    }
    if (passState.inputValue !== passState.inputValue.trim()) {
      setPasswordHelperText(t('passwordNoEdgeWhitespace'));
      return;
    }
    if (passState.inputValue.length < 6 || passState.inputValue.length > 50) {
      setPasswordHelperText(t('passwordLength'));
      return;
    }
    setLoading(true);
    const languageOptions = localStorage.getItem('i18nextLng') || i18n.languages[0] || 'de';
    let language = languageOptions.split('-')[0];
    language = ['en', 'de'].includes(language) ? language : 'de';
    const res = await register(
      buildRegisterPayload({
        firstName,
        lastName,
        email: emailState.inputValue,
        password: passState.inputValue,
        companyName,
        language,
        attribution,
        consentMarketingEmail,
        consentAdMeasurement,
      })
    );
    setLoading(false);
    if (res.error) {
      if (res.error.message === ErrorMessages.USER_ALREADY_EXISTS) {
        setEmailHelperText(t('emailAlreadyExists'));
      } else {
        handleOpenErrorDialog(res?.error, '/register', 'post');
      }
    } else {
      handleSignIn();
    }
  };

  const handleSignIn = async () => {
    const loginRes = await login({
      email: emailState.inputValue,
      password: passState.inputValue,
    });

    if (loginRes.error) {
      handleOpenErrorDialog(loginRes?.error, '/login', 'post');
    }

    if (loginRes.data) {
      // Store token immediately so subsequent API calls have auth
      setToken(loginRes.data.token);

      const userInfoRes = await getUserInfo();

      if (!userInfoRes.error) {
        const [productsRes, licensesRes] = await Promise.all([getAvailableProducts(), getFullLicense()]);

        const userInfo: UserInfo = new UserInfo(userInfoRes);
        const products = !productsRes.error ? productsRes.products : [];
        const licArr = Array.isArray(licensesRes) ? licensesRes : [];

        signIn(loginRes.data.user, userInfo, products, loginRes.data.token, licArr);

        // Track registration completion
        await trackRegistrationComplete(userInfo.id, userInfo.email);
      } else {
        handleOpenErrorDialog(userInfoRes?.error || { message: 'User ID missing from /me response.' }, '/users/me', 'get');
      }

      i18n.changeLanguage(loginRes.data.user.language);
      extensionSignIn(loginRes.data.token, loginRes.data.user);
    }
  };

  const renderStat = (badge: string, text: string, badgeClass: string) => (
    <Stack direction={'row'} alignItems={'center'} gap={1.5}>
      <span className={badgeClass}>{badge}</span>
      <span className={classes.statText}>{text}</span>
    </Stack>
  );

  return (
    <div className={classes.wrapper}>
      <div className={classes.marketingPanel}>
        <Stack gap={1.75} className={classes.marketingBlock}>
          <Typography className={classes.marketingHeadline}>{t('authSignupHeadline')}</Typography>
          <Typography className={classes.marketingSub}>{t('authSignupSubheadline')}</Typography>
        </Stack>
        <Stack direction={'row'} gap={1.5} flexWrap={'wrap'}>
          <span className={`${classes.pill} ${classes.pillRecruiting}`}>{t('authMarketingPillRecruiting')}</span>
          <span className={`${classes.pill} ${classes.pillLead}`}>{t('authMarketingPillLead')}</span>
        </Stack>
        <Stack gap={1.75} className={classes.marketingBlock}>
          {renderStat(t('authMarketingStatBadge1'), t('authMarketingStatText1'), classes.benefitHighlightOrange)}
          {renderStat(t('authMarketingStatBadge2'), t('authMarketingStatText2'), classes.benefitHighlightPink)}
          {renderStat(t('authMarketingStatBadge3'), t('authMarketingStatText3'), classes.benefitHighlightOrange)}
        </Stack>
        <img
          className={classes.heroImage}
          src={'/dashboard/images/marketing/auth-hero.png'}
          alt={'Outreach that Converts'}
          data-fallback={'0'}
          onError={(e) => {
            const el = e.currentTarget as HTMLImageElement;
            const step = el.getAttribute('data-fallback') || '0';
            if (step === '0') {
              el.setAttribute('data-fallback', '1');
              el.src = '/images/marketing/auth-hero.png';
            } else if (step === '1') {
              el.setAttribute('data-fallback', '2');
              el.src = '/dashboard/images/lead/lead_logo.png';
            }
          }}
        />
      </div>

      <div className={classes.register}>
        <Stack gap={3} className={classes.form}>
          <Stack gap={0.75}>
            <Typography className={classes.formTitle}>{t('authSignupFormTitle')}</Typography>
            <Typography className={classes.formSubtitle}>{t('authSignupFormSubtitle')}</Typography>
          </Stack>

          <Stack gap={2}>
            <Stack direction={'row'} gap={1.5}>
              <div className={cx(classes.field, classes.fieldHalf)}>
                <Typography className={classes.fieldLabel}>{t('firstname')}</Typography>
                <PaiTextField
                  value={firstName}
                  onChange={handleFirstNameChange}
                  error={firstName.trim().length > 128}
                  errorText={t('firstNameLength')}
                />
              </div>
              <div className={cx(classes.field, classes.fieldHalf)}>
                <Typography className={classes.fieldLabel}>{t('lastname')}</Typography>
                <PaiTextField
                  value={lastName}
                  onChange={handleLastNameChange}
                  error={lastName.trim().length > 128}
                  errorText={t('lastNameLength')}
                />
              </div>
            </Stack>
            <div className={classes.field}>
              <Typography className={classes.fieldLabel}>{t('authFieldEmailLabel')}</Typography>
              <PaiTextField
                placeholder={t('authFieldEmailPlaceholder')}
                value={emailState.inputValue}
                onChange={onEmailFormChange}
                error={emailHelperText !== ''}
                errorText={emailHelperText}
              />
            </div>
            <div className={classes.field}>
              <Typography className={classes.fieldLabel}>{t('companyName')}</Typography>
              <PaiTextField
                placeholder={t('authFieldCompanyPlaceholder')}
                value={companyName}
                onChange={handleCompanyChange}
                error={companyName.length >= 30}
                errorText={t('companyNameWarning')}
              />
            </div>
            <div className={classes.field}>
              <Typography className={classes.fieldLabel}>{t('password')}</Typography>
              <PaiTextField
                placeholder={t('authFieldPasswordPlaceholder')}
                value={passState.inputValue}
                onChange={onPassFormChange}
                type={'password'}
                error={passwordHelperText !== ''}
                errorText={passwordHelperText}
              />
            </div>
          </Stack>

          <Stack gap={1.25}>
            <div className={classes.consent}>
              <PaiCheckbox
                checked={termsAccepted}
                dense={true}
                label={t('acceptTermsAndPrivacy', 'Ich habe die AGB und die Datenschutzerklärung gelesen und stimme ihnen zu.')}
                onChange={(event) => setTermsAccepted(event.target.checked)}
              />
            </div>
            <Stack direction={'row'} gap={2} flexWrap={'wrap'} className={classes.legalLinks}>
              <PaiLink text={t('termsOfUse')} size={'xs'} variant={'primary'} underline={'always'} href={agbUrl} target={'_blank'} />
              <PaiLink text={t('privacyPolicy')} size={'xs'} variant={'primary'} underline={'always'} href={privacyUrl} target={'_blank'} />
              <PaiLink
                text={t('dataProcessingAgreement', 'Auftragsverarbeitung')}
                size={'xs'}
                variant={'primary'}
                underline={'always'}
                href={dpaUrl}
                target={'_blank'}
              />
            </Stack>
            {/* TODO(legal): wording of both optional consents is pending legal review (Rechtliches im Plan
                engagement-center-phase-1.md). Bump OPTIONAL_CONSENT_VERSION in the API when it changes. */}
            <div className={classes.consent}>
              <PaiCheckbox
                checked={consentMarketingEmail}
                dense={true}
                label={t('engSignupConsentMarketing')}
                onChange={(event) => setConsentMarketingEmail(event.target.checked)}
              />
            </div>
            <div className={classes.consent}>
              <PaiCheckbox
                checked={consentAdMeasurement}
                dense={true}
                label={t('engSignupConsentAdMeasurement')}
                onChange={(event) => setConsentAdMeasurement(event.target.checked)}
              />
            </div>
          </Stack>

          <PaiButton
            text={t('signup')}
            action={handleSignUp}
            size={'large'}
            fullWidth={true}
            loading={loading}
            disabled={!isAllValid()}
            style={{ height: '52px', borderRadius: '8px', fontSize: '1.0625rem' }}
          />
          <Stack direction={'row'} justifyContent={'center'}>
            <PaiLink text={t('backToLogin')} size={'sm'} variant={'primary'} underline={'always'} action={() => navigate(Routes.SIGNIN)} />
          </Stack>
        </Stack>
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  wrapper: {
    display: 'flex',
    minHeight: '100vh',
  },
  marketingPanel: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '32px',
    flex: '0 0 55%',
    maxWidth: '55%',
    padding: '56px',
    backgroundColor: '#0E0644',
    color: '#FFF',
    minHeight: '100vh',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  marketingBlock: {
    maxWidth: '520px',
  },
  marketingHeadline: {
    fontSize: theme.typography.pxToRem(32),
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.4px',
  },
  marketingSub: {
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1.6,
    opacity: 0.85,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '48px',
    padding: '0 22px',
    borderRadius: '12px',
    color: '#FFF',
    fontFamily: 'Manrope, sans-serif',
    fontSize: theme.typography.pxToRem(17),
    fontWeight: 400,
    boxSizing: 'border-box',
  },
  pillRecruiting: {
    backgroundImage: 'linear-gradient(135deg, rgb(255, 165, 2), rgb(255, 202, 56))',
    boxShadow: 'rgba(255, 255, 255, 0.4) 0px 0px 0px 1px inset',
  },
  pillLead: {
    backgroundImage: 'linear-gradient(135deg, rgb(255, 10, 113), rgb(255, 128, 181))',
    boxShadow: 'rgba(255, 255, 255, 0.2) 0px 0px 0px 1px inset',
  },
  statText: {
    fontSize: theme.typography.pxToRem(15),
    opacity: 0.95,
  },
  benefitHighlightOrange: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '8px',
    backgroundImage: 'linear-gradient(135deg, rgb(255, 165, 2), rgb(255, 202, 56))',
    color: '#0E0644',
    fontWeight: 800,
    fontSize: theme.typography.pxToRem(15),
    boxShadow: 'rgba(255,255,255,0.4) 0 0 0 1px inset',
  },
  benefitHighlightPink: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '8px',
    backgroundImage: 'linear-gradient(135deg, rgb(255, 10, 113), rgb(255, 128, 181))',
    color: '#0E0644',
    fontWeight: 800,
    fontSize: theme.typography.pxToRem(15),
    boxShadow: 'rgba(255,255,255,0.3) 0 0 0 1px inset',
  },
  heroImage: {
    width: '100%',
    maxWidth: '520px',
    height: '240px',
    borderRadius: '12px',
    objectFit: 'contain',
    objectPosition: 'left center',
    display: 'block',
  },
  register: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    padding: '40px',
    [theme.breakpoints.down('sm')]: {
      padding: '24px 16px',
    },
  },
  form: {
    width: '100%',
    maxWidth: '420px',
  },
  formTitle: {
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 500,
    letterSpacing: '-0.2px',
  },
  formSubtitle: {
    fontSize: theme.typography.pxToRem(14),
    color: '#707070',
  },
  field: {
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    '& .MuiFormControl-root': {
      borderColor: '#E3E3E8',
      borderRadius: '8px',
    },
    '& .MuiInputBase-root': {
      borderRadius: '8px',
    },
    '& .MuiInputBase-input': {
      borderRadius: '8px',
      minHeight: 'calc(44px - 16px)',
    },
    '& .MuiFormControl-root:focus-within': {
      borderColor: theme.palette.primary.main,
    },
    '& .MuiFormControl-root:has(.Mui-error)': {
      borderColor: theme.palette.error.main,
    },
  },
  fieldHalf: {
    flex: 1,
  },
  fieldLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: '#707070',
  },
  consent: {
    '& .MuiFormControlLabel-root': {
      alignItems: 'flex-start',
    },
    '& .MuiFormControlLabel-label': {
      fontSize: theme.typography.pxToRem(13),
      lineHeight: 1.5,
      color: '#555',
    },
    '& .MuiCheckbox-root': {
      marginTop: '1px',
    },
  },
  legalLinks: {
    paddingLeft: '28px',
  },
}));

export default SignUp;
